'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Heart, Shield, Lightbulb, Target } from 'lucide-react';
import { TestimonialsSection } from '@/components/public/testimonials-section';
import { StatsBand, type Stat } from '@/components/public/stats-band';
import { Reveal } from '@/components/public/reveal';
import type { Testimonial } from '@/data/testimonials';
import { COMPANY_STATS } from '@/data/company-facts';

// Icon + i18n key per value; copy lives in `public.about.values.<key>`.
const values = [
  { icon: Target, key: 'excellence' },
  { icon: Heart, key: 'inclusivity' },
  { icon: Shield, key: 'integrity' },
  { icon: Lightbulb, key: 'innovation' },
] as const;

export function AboutContent({ reviews }: { reviews: Testimonial[] }) {
  const t = useTranslations('public.about');

  // Numbers live in the shared COMPANY_STATS constant (single source of truth,
  // also ingested by Sunny's company-knowledge builder) to prevent stat drift.
  const stats: Stat[] = COMPANY_STATS.map((s) => ({
    value: s.value,
    suffix: s.suffix,
    label: t(s.labelKey),
  }));

  return (
    <>
      {/* Hero banner */}
      <section className="relative overflow-hidden bg-[var(--eifo-bg-light)] py-16 md:py-24">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-eifo-accent/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <h1 className="font-display text-4xl font-bold text-eifo-primary md:text-5xl">
            {t('heroTitle')}
          </h1>
          <p className="mt-6 text-lg text-gray-600">{t('heroSubtitle')}</p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 md:grid-cols-2">
          <Reveal>
            <h2 className="font-display mb-6 text-3xl font-bold text-eifo-primary">{t('missionHeading')}</h2>
            <p className="text-gray-600 leading-relaxed">{t('missionBody')}</p>
          </Reveal>
          <Reveal delayMs={120} className="flex justify-center">
            <div className="relative">
              <div
                aria-hidden
                className="absolute inset-0 -z-10 m-auto h-64 w-64 rounded-full bg-eifo-orange/10 blur-2xl"
              />
              <Image
                src="/images/student-backpack.png"
                alt={t('studentBackpackAlt')}
                width={1000}
                height={1417}
                className="animate-eifo-float w-64 drop-shadow-xl md:w-72"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[var(--eifo-bg-light)] py-16">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-display mb-12 text-center text-3xl font-bold text-eifo-primary">
            {t('valuesHeading')}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.key} delayMs={(i % 4) * 80}>
                <div className="h-full rounded-xl border bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-eifo-orange/10">
                    <v.icon className="h-6 w-6 text-eifo-orange" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-eifo-primary">
                    {t(`values.${v.key}.title`)}
                  </h3>
                  <p className="text-sm text-gray-600">{t(`values.${v.key}.description`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-display mb-6 text-3xl font-bold text-eifo-primary">{t('visionHeading')}</h2>
          <p className="text-gray-600 leading-relaxed">{t('visionBody')}</p>
        </div>
      </section>

      {/* Stats */}
      <StatsBand stats={stats} />

      {/* Educator teaser */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-display mb-4 text-3xl font-bold text-eifo-primary">
            {t('educatorTeaserHeading')}
          </h2>
          <p className="mb-8 text-gray-600">{t('educatorTeaserBody')}</p>
          <Button
            asChild
            size="lg"
            className="bg-eifo-orange text-white transition-transform hover:scale-105 hover:bg-eifo-orange/90"
          >
            <Link href="/educators">{t('educatorTeaserCta')}</Link>
          </Button>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection reviews={reviews} />
    </>
  );
}
